import { useEffect, useMemo, useState } from "react";
import FAQSection from "../home/FAQSection";
import Footer from "../home/Footer";
import Header from "../home/Header";
import MiniCart from "../home/MiniCart";
import TestimonialsSection from "../home/TestimonialsSection";
import USPSection from "../home/USPSection";
import VideosSection from "../home/VideosSection";
import { CheckCircleIcon } from "../home/HomeIcons";
import SEOHead from "../seo/SEOHead";

function ApplicationHero({ page }) {
  return (
    <section
      className="hero"
      aria-label={`${page.title} banner`}
      style={{ background: `url(${page.heroBackground}) center/cover no-repeat` }}
    >
      <div className="hero-bg" aria-hidden="true" />
      <div className="hero-inner">
        <div className="hero-content-box">
          <div className="hero-eyebrow fade-up">{page.eyebrow}</div>
          <h1 className="hero-title fade-up delay-1">
            {page.heroTitle}
            <em>{page.heroAccent}</em>
          </h1>
          <p className="hero-sub fade-up delay-2">{page.heroText}</p>
          <div className="hero-actions fade-up delay-3">
            {page.heroCtas.map((cta) => (
              <a
                key={cta.href}
                href={cta.href}
                className={cta.variant === "gold" ? "btn-gold" : "btn-outline-green"}
                style={cta.variant === "outline" ? { color: "rgba(255,255,255,0.85)", borderColor: "rgba(255,255,255,0.35)" } : undefined}
              >
                {cta.label}
              </a>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function ApplicationStorySections({ page }) {
  return (
    <section className="section product-story-section">
      <div className="section-inner">
        <div className="product-story-stack">
          {page.sections.map((section, index) => (
            <article key={section.title} className={`product-story-split${section.imageSide === "right" ? " reverse" : ""}`}>
              <div className="product-story-media">
                <img src={section.image} alt={section.imageAlt} />
              </div>
              <div className="product-story-copy">
                <span className="section-label">{section.eyebrow}</span>
                <h3>{section.title}</h3>
                {section.paragraphs.map((paragraph) => (
                  <p key={`${section.title}-${paragraph}`}>{paragraph}</p>
                ))}
                <ul>
                  {section.bullets.map((bullet) => (
                    <li key={`${section.title}-${bullet}`}>
                      <CheckCircleIcon className="ui-icon feature-check-icon" />
                      <span>{bullet}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function ApplicationBannerSection({ banner }) {
  return (
    <section className="section">
      <div className="section-inner">
        <article
          className="product-story-banner"
          style={{ backgroundImage: `linear-gradient(rgba(19, 55, 24, 0.62), rgba(19, 55, 24, 0.62)), url(${banner.backgroundImage})` }}
        >
          <span className="section-label">{banner.eyebrow}</span>
          <h3>{banner.title}</h3>
          <p>{banner.text}</p>
        </article>
      </div>
    </section>
  );
}

export default function ApplicationPageTemplate({ page }) {
  const [scrolled, setScrolled] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);
  const cartItems = [];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const schema = useMemo(() => ({
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": `${page.title} Organic Plant Care`,
    "description": page.heroText,
    "url": `https://parasorganics.com/applications/${page.slug}`
  }), [page]);

  return (
    <>
      <SEOHead
        title={`${page.title} Organic Plant Care`}
        description={page.heroText}
        path={`/applications/${page.slug}`}
        image={page.heroBackground}
        keywords={[
          `${page.title} organic fertilizer`,
          `${page.title} vermicompost`,
          `${page.title} vermiwash`,
          "Paras Organics",
          "organic fertilizer",
          "soil health",
          "plant growth"
        ]}
        schema={schema}
      />
      <Header scrolled={scrolled} cartItems={cartItems} onCartClick={() => setCartOpen((open) => !open)} />
      {cartOpen && <MiniCart cartItems={cartItems} onClose={() => setCartOpen(false)} />}
      <main>
        <ApplicationHero page={page} />
        <USPSection />
        <ApplicationStorySections page={page} />
        <ApplicationBannerSection banner={page.banner} />
        <VideosSection />
        <TestimonialsSection />
        <FAQSection />
      </main>
      <Footer />
    </>
  );
}
